/**
 * Returns a human-readable relative time string for a given date.
 * Falls back to absolute date for very old timestamps (>30 days).
 *
 * A2F-1019: the >30-day fallback now renders the calendar date in the
 * org display timezone (IANA string) rather than browser-local.  Pass
 * `tz` from `useDisplayTimezone()` at the callsite.  Omitting `tz` is
 * safe — it falls back to UTC (consistent and labeled).
 */
import { formatInstant } from './formatInstant';

export function timeAgo(dateStr: string, tz?: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;

  // >30 days: render the org-TZ calendar date (no time, no label) so the
  // fallback stays in sync with the org's declared timezone.
  return `on ${formatInstant(dateStr, tz, { dateOnly: true })}`;
}
